// FinKloz — Bank Statement Parser (browser-side)
// Parses Excel/CSV bank statements, auto-detects bank and account.
// Supports: RBC (French), Desjardins (French), Float Financial (English)

import * as XLSX from 'xlsx';
import { getAccessTokenSync } from '../hooks/useAccessToken';

// ─── Known bank accounts (default configuration) ───
export const BANK_ACCOUNTS = [
  { accountNo: '11010', name: 'RBC - CAD Chequing - Operating', currency: 'CAD', bank: 'RBC', glAccount: '11010' },
  { accountNo: '11020', name: 'RBC - CAD Savings', currency: 'CAD', bank: 'RBC', glAccount: '11020' },
  { accountNo: '11030', name: 'RBC - USD Chequing - Operating', currency: 'USD', bank: 'RBC', glAccount: '11030' },
  { accountNo: '11040', name: 'RBC - USD Savings', currency: 'USD', bank: 'RBC', glAccount: '11040' },
  { accountNo: '11070', name: 'Float - CAD Chequing', currency: 'CAD', bank: 'Float', glAccount: '11070' },
  { accountNo: '11080', name: 'Float - USD Chequing', currency: 'USD', bank: 'Float', glAccount: '11080' },
  { accountNo: '11100', name: 'Desjardins - CAD Chequing', currency: 'CAD', bank: 'Desjardins', glAccount: '11100' },
  { accountNo: '11110', name: 'Desjardins - USD Savings', currency: 'USD', bank: 'Desjardins', glAccount: '11110' },
];

// ─── Header detection patterns (EN + FR) ───
const HEADER_PATTERNS = {
  date: /\b(date|date\s*d['']?op|transaction\s*date|posted|valeur)\b/i,
  description: /\b(description|libell[e\u00e9]|detail|particulars|memo)\b/i,
  debit: /\b(debit|d[e\u00e9]bit|retrait|withdrawal|cheques?\s*et\s*d[e\u00e9]bits)\b/i,
  credit: /\b(credit|cr[e\u00e9]dit|d[e\u00e9]p[o\u00f4]t|deposit|d[e\u00e9]p[o\u00f4]ts?\s*et\s*cr[e\u00e9]dits)\b/i,
  balance: /\b(balance|solde|running|cumul)\b/i,
};

// ─── Bank detection patterns ───
const BANK_DETECT = [
  { pattern: /\brbc\b|royal\s*bank|banque\s*royale/i, bank: 'RBC' },
  { pattern: /\bdesjardins\b|caisse\s*populaire|mouvement\s*desjardins/i, bank: 'Desjardins' },
  { pattern: /\bfloat\b|float\s*financial|receipt-store/i, bank: 'Float' },
];

// ─── Currency detection ───
const CURRENCY_DETECT = [
  { pattern: /\busd\b|us\s*dollar|dollar\s*am[e\u00e9]ricain|\$\s*us/i, currency: 'USD' },
  { pattern: /\bcad\b|can\s*dollar|dollar\s*canadien/i, currency: 'CAD' },
];

// ─── Parse a number from various formats ───
// Handles: "92 186,69" (French), "$1,024.80", "(141.42)", "1024.80"
function parseAmount(val) {
  if (val === null || val === undefined || val === '') return 0;
  if (typeof val === 'number') return val;
  let s = String(val).trim();
  // Detect negative in parentheses: ($141.42) or (141.42)
  const isNeg = /^\(.*\)$/.test(s) || /^-/.test(s);
  s = s.replace(/[()$\s]/g, '');
  // French format: "92 186,69" → uses comma as decimal
  if (/^\d{1,3}(\s\d{3})*,\d{2}$/.test(s)) {
    s = s.replace(/\s/g, '').replace(',', '.');
  } else {
    // English format: "1,024.80" → remove commas
    s = s.replace(/,/g, '');
  }
  s = s.replace(/[^0-9.-]/g, '');
  const n = parseFloat(s) || 0;
  return isNeg ? -Math.abs(n) : n;
}

// ─── Parse date from various formats ───
function parseDate(val) {
  if (!val) return null;
  const s = String(val).trim();
  // ISO: 2026-01-02
  let m = s.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (m) return `${m[1]}-${m[2].padStart(2, '0')}-${m[3].padStart(2, '0')}`;
  // US: 01/02/2026 or 1/2/2026
  m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  if (m) return `${m[3]}-${m[1].padStart(2, '0')}-${m[2].padStart(2, '0')}`;
  // FR: 02/01/2026 (day/month/year) — ambiguous, try US first
  // Excel serial number
  if (typeof val === 'number' && val > 25000 && val < 60000) {
    const d = new Date((val - 25569) * 86400 * 1000);
    return d.toISOString().split('T')[0];
  }
  return null;
}

// ─── Detect columns from header row ───
function detectColumns(row) {
  const cols = { date: -1, description: -1, debit: -1, credit: -1, balance: -1 };
  row.forEach((cell, i) => {
    const val = String(cell || '').trim();
    if (!val) return;
    for (const [key, pattern] of Object.entries(HEADER_PATTERNS)) {
      if (pattern.test(val) && cols[key] === -1) {
        cols[key] = i;
      }
    }
  });
  return cols;
}

// ─── Auto-detect bank from file content ───
function detectBank(rows) {
  const text = rows.slice(0, 20).flat().join(' ');
  for (const { pattern, bank } of BANK_DETECT) {
    if (pattern.test(text)) return bank;
  }
  return null;
}

// ─── Auto-detect currency from file content ───
function detectCurrency(rows) {
  const text = rows.slice(0, 20).flat().join(' ');
  for (const { pattern, currency } of CURRENCY_DETECT) {
    if (pattern.test(text)) return currency;
  }
  return 'CAD'; // default
}

// ─── Parse transactions from sheet data ───
function parseTransactions(rows) {
  // Find header row
  let headerIdx = -1;
  let cols = null;

  for (let i = 0; i < Math.min(rows.length, 20); i++) {
    const row = rows[i];
    if (!row || row.length < 3) continue;
    const detected = detectColumns(row.map(c => String(c || '').trim()));
    if (detected.date !== -1 && (detected.debit !== -1 || detected.credit !== -1 || detected.balance !== -1)) {
      headerIdx = i;
      cols = detected;
      break;
    }
  }

  if (headerIdx === -1 || !cols || cols.date === -1) return [];

  const transactions = [];
  let lastDate = null;

  for (let i = headerIdx + 1; i < rows.length; i++) {
    const row = rows[i];
    if (!row || row.length < 3) continue;

    const dateVal = cols.date !== -1 ? row[cols.date] : null;
    const date = parseDate(dateVal) || lastDate;
    if (!date) continue;
    lastDate = date;

    const description = cols.description !== -1 ? String(row[cols.description] || '').trim() : '';
    if (!description && cols.debit === -1) continue;

    let debit = 0, credit = 0, balance = null;
    if (cols.debit !== -1) debit = Math.abs(parseAmount(row[cols.debit]));
    if (cols.credit !== -1) credit = Math.abs(parseAmount(row[cols.credit]));
    if (cols.balance !== -1) balance = parseAmount(row[cols.balance]);

    // Skip rows with no amounts (section headers, subtotals labeled "TOTAL")
    if (debit === 0 && credit === 0 && balance === null) continue;
    if (/^(total|sous-total|subtotal)/i.test(description)) continue;

    transactions.push({
      id: `stmt-${i}`,
      date,
      description,
      debit,
      credit,
      balance,
    });
  }

  return transactions;
}

// ─── Extract metadata (opening/closing balance) ───
function extractMetadata(rows, transactions, detectedBank, detectedCurrency) {
  let openingBalance = null;
  let closingBalance = null;
  let periodStart = null;
  let periodEnd = null;

  // Try to find opening/closing from header area
  for (let i = 0; i < Math.min(rows.length, 20); i++) {
    const text = (rows[i] || []).map(c => String(c || '')).join(' ').toLowerCase();
    if (/opening|ouverture|solde\s*d['\u2019]?ouverture|beginning/i.test(text)) {
      const nums = (rows[i] || []).filter(c => typeof c === 'number' || /[\d.,]+/.test(String(c || '')));
      if (nums.length > 0) openingBalance = parseAmount(nums[nums.length - 1]);
    }
    if (/closing|cl[o\u00f4]ture|solde\s*de?\s*cl[o\u00f4]ture|ending/i.test(text)) {
      const nums = (rows[i] || []).filter(c => typeof c === 'number' || /[\d.,]+/.test(String(c || '')));
      if (nums.length > 0) closingBalance = parseAmount(nums[nums.length - 1]);
    }
  }

  // Fallback: derive from transactions
  if (transactions.length > 0) {
    if (!periodStart) periodStart = transactions[0].date;
    if (!periodEnd) periodEnd = transactions[transactions.length - 1].date;
    if (closingBalance === null && transactions[transactions.length - 1].balance !== null) {
      closingBalance = transactions[transactions.length - 1].balance;
    }
    if (openingBalance === null && transactions[0].balance !== null) {
      // opening = first balance - first credit + first debit
      openingBalance = transactions[0].balance - transactions[0].credit + transactions[0].debit;
    }
  }

  return {
    bankName: detectedBank,
    currency: detectedCurrency,
    periodStart,
    periodEnd,
    openingBalance: openingBalance || 0,
    closingBalance: closingBalance || 0,
  };
}

// ─── Match parsed statement to a known bank account ───
export function matchToAccount(metadata, accounts = BANK_ACCOUNTS) {
  const candidates = accounts.filter(a => {
    const bankMatch = !metadata.bankName || a.bank.toLowerCase() === metadata.bankName.toLowerCase();
    const currMatch = !metadata.currency || a.currency === metadata.currency;
    return bankMatch && currMatch;
  });
  if (candidates.length === 1) return candidates[0];
  return null; // ambiguous — user must pick
}

// ─── PDF parsing via Supabase Edge Function (Claude AI vision) ───
async function parsePDF(file) {
  const SB_URL = process.env.REACT_APP_SUPABASE_URL;
  const SB_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

  if (!SB_URL || !SB_KEY) {
    throw new Error('Supabase not configured. Set REACT_APP_SUPABASE_URL and REACT_APP_SUPABASE_ANON_KEY in .env');
  }

  // Get auth token for the edge function (requires JWT)
  const token = getAccessTokenSync() || SB_KEY;

  const formData = new FormData();
  formData.append('file', file);

  const resp = await fetch(`${SB_URL}/functions/v1/parse-bank-statement`, {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
    body: formData,
  });

  if (!resp.ok) {
    const err = await resp.json().catch(() => ({ error: `HTTP ${resp.status}` }));
    throw new Error(err.error || err.detail || `PDF parsing failed: ${resp.status}`);
  }

  return await resp.json();
}

// ─── Main entry point: parse a bank statement file ───
export async function parseBankStatement(file) {
  const ext = file.name.toLowerCase().split('.').pop();

  if (ext === 'pdf') {
    // Use Claude AI via Supabase Edge Function
    const raw = await parsePDF(file);
    // Normalize snake_case from Claude to camelCase used by frontend
    const meta = raw.metadata || {};
    return {
      metadata: {
        bankName: meta.bank_name || meta.bankName || null,
        accountNumber: meta.account_number || meta.accountNumber || null,
        currency: meta.currency || 'CAD',
        periodStart: meta.period_start || meta.periodStart || null,
        periodEnd: meta.period_end || meta.periodEnd || null,
        openingBalance: parseFloat(meta.opening_balance ?? meta.openingBalance ?? 0) || 0,
        closingBalance: parseFloat(meta.closing_balance ?? meta.closingBalance ?? 0) || 0,
      },
      transactions: (raw.transactions || [])
        .filter(t => {
          // Filter out balance rows -- these are not movements
          const desc = (t.description || '').trim().toLowerCase();
          if (/balance|solde|bal\b/i.test(desc) && /(start|end|open|clos|begin|initial|final|ouverture|cl[oô]ture)/i.test(desc)) return false;
          // Filter out account type headers and investment sections (Desjardins)
          if (/^compte\s*(d['']?[e\u00e9]pargne|courant|ch[e\u00e8]que)/i.test(desc)) return false;
          // Filter out GIC/investment section from Desjardins statements
          // These transactions belong to a different GL account (11200 investments, not 11110 chequing)
          if (/\b(d[e\u00e9]p[o\u00f4]t\s*[a\u00e0]\s*terme|placement\s*(garanti|rachetable)|cpg|gic)\b/i.test(desc)) return false;
          if (/\b(renouvell.*placement|maturit[e\u00e9]|rendement.*int[e\u00e9]r[e\u00ea]t|int[e\u00e9]r[e\u00ea]t.*cpg)\b/i.test(desc)) return false;
          // Filter out zero-amount rows (no actual movement)
          const d = parseFloat(t.debit) || 0;
          const c = parseFloat(t.credit) || 0;
          if (d === 0 && c === 0) return false;
          return true;
        })
        .map(t => {
          // Post-process: fix sign classification for known patterns
          // Payments are ALWAYS money OUT (debit), deposits are ALWAYS money IN (credit)
          const desc = (t.description || '').toLowerCase();
          let debit = parseFloat(t.debit) || 0;
          let credit = parseFloat(t.credit) || 0;
          const total = debit + credit;

          const isPaymentOut = /\b(paiement|payment|pymt|wire|virement\s*(sortant|envoy)|retrait|withdrawal|frais|fee|charge)\b/i.test(desc);
          const isDepositIn = /\b(d[eé]p[oô]t\s*(re[cç]u|entrant)|deposit\s*received|int[eé]r[eê]t|interest\s*earned)\b/i.test(desc);

          if (isPaymentOut && credit > 0 && debit === 0) {
            // Misclassified: payment showing as credit, should be debit
            debit = credit;
            credit = 0;
          } else if (isDepositIn && debit > 0 && credit === 0) {
            // Misclassified: deposit showing as debit, should be credit
            credit = debit;
            debit = 0;
          }

          return { ...t, debit, credit };
        })
        .map((t, i) => ({
        id: t.id || `stmt-${i}`,
        date: t.date,
        description: t.description || '',
        debit: parseFloat(t.debit) || 0,
        credit: parseFloat(t.credit) || 0,
        balance: t.running_balance != null ? parseFloat(t.running_balance) : null,
      })),
    };
  }

  let rows;
  if (ext === 'csv') {
    const text = await file.text();
    rows = text.split(/\r?\n/).map(line => {
      const sep = line.includes(';') ? ';' : ',';
      return line.split(sep).map(cell => cell.replace(/^"(.*)"$/, '$1').trim());
    });
  } else if (['xls', 'xlsx', 'xlsm'].includes(ext)) {
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: 'array' });
    const sheet = workbook.Sheets[workbook.SheetNames[0]];
    rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
  } else {
    throw new Error(`Unsupported format: .${ext}. Use CSV, XLS, or XLSX.`);
  }

  const detectedBank = detectBank(rows);
  const detectedCurrency = detectCurrency(rows);
  const transactions = parseTransactions(rows);

  if (transactions.length === 0) {
    throw new Error('No transactions found. Check that the file has columns for Date, Description, and Debit/Credit.');
  }

  const metadata = extractMetadata(rows, transactions, detectedBank, detectedCurrency);

  return { metadata, transactions };
}

// ─── Parse a multi-sheet bank reconciliation workbook ───
// For files like bank_reconciliation_jan2026_.xlsx that have one sheet per account
export async function parseMultiSheetStatement(file) {
  const ext = file.name.toLowerCase().split('.').pop();
  if (!['xls', 'xlsx', 'xlsm'].includes(ext)) {
    throw new Error('Multi-sheet parsing only supported for Excel files.');
  }

  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array' });
  const results = {};

  for (const sheetName of workbook.SheetNames) {
    // Skip non-account sheets
    if (/^(instructions|summary|notes)/i.test(sheetName)) continue;
    // Account sheets are named by account number (e.g., "11010")
    if (!/^\d{4,5}$/.test(sheetName.trim())) continue;

    const sheet = workbook.Sheets[sheetName];
    const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
    const detectedBank = detectBank(rows);
    const detectedCurrency = detectCurrency(rows);
    const transactions = parseTransactions(rows);
    const metadata = extractMetadata(rows, transactions, detectedBank, detectedCurrency);

    results[sheetName.trim()] = { metadata, transactions };
  }

  return results;
}
